import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaExpand, FaTimes, FaPlayCircle } from 'react-icons/fa';

const INITIAL_THUMB_COUNT = 5; // Only render 5 thumbs initially — prevents prerender from loading 40+ images

const ImageGallery = ({ images = [], videos = [], selectedIndex = 0, onSelectImage, vehicleName = '' }) => {
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(selectedIndex);
  const [showAllThumbnails, setShowAllThumbnails] = useState(false);
  
  const autoScrollRef = useRef(null);
  const userInteractedRef = useRef(false);

  // Detect video URLs by extension
  const isVideoUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.avi') || lower.includes('/video/');
  };

  // Ensure arrays are valid - handle both arrays and single values
  const rawVideos = Array.isArray(videos) ? videos : (videos ? [videos] : []);
  const rawImages = Array.isArray(images) ? images : (images ? [images] : []);
  
  // Separate: pull any video URLs out of images array, merge into videos
  const pureImages = rawImages.filter(i => i && !isVideoUrl(i));
  const allVideos = [
    ...rawVideos.filter(v => v && v !== null),
    ...rawImages.filter(i => i && isVideoUrl(i))
  ];
  
  // Combine images and videos (images first — Google VLA requires a photo as the primary image)
  const mediaItems = [
    ...pureImages.map(i => ({ type: 'image', url: i })),
    ...allVideos.filter(v => v !== null).map(v => ({ type: 'video', url: v }))
  ];
  
  // Default placeholder image if no media provided
  const galleryMedia = mediaItems.length > 0 ? mediaItems : [
    { type: 'image', url: '/assets/assets/images/business-shop-sign.webp' }
  ];
  
  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? galleryMedia.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    if (newIndex >= INITIAL_THUMB_COUNT) setShowAllThumbnails(true);
    onSelectImage && onSelectImage(newIndex);
  };
  
  const handleNext = () => {
    const newIndex = currentIndex === galleryMedia.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    if (newIndex >= INITIAL_THUMB_COUNT) setShowAllThumbnails(true);
    onSelectImage && onSelectImage(newIndex);
  };
  
  const handleThumbnailClick = (index) => {
    userInteractedRef.current = true;
    setCurrentIndex(index);
    onSelectImage && onSelectImage(index);
  };

  // Auto-scroll disabled — it causes Google's crawler to screenshot a broken
  // fallback image instead of the correct first vehicle photo.

  return (
    <>
      <div className="relative">
        {/* Main Media Display */}
        <motion.div 
          className="relative aspect-video xl:aspect-[21/9] 2xl:aspect-[2.5/1] rounded-xl overflow-hidden bg-gray-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <AnimatePresence mode="wait">
            {galleryMedia[currentIndex]?.type === 'video' ? (
              <motion.video
                key={currentIndex}
                src={galleryMedia[currentIndex].url}
                className="w-full h-full object-contain"
                controls
                autoPlay
                muted
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                onError={(e) => {
                  console.error('Video failed to load:', e);
                }}
              />
            ) : (
              <motion.img
                key={currentIndex}
                src={galleryMedia[currentIndex]?.url}
                alt={vehicleName ? `${vehicleName} - photo ${currentIndex + 1} of ${galleryMedia.length}` : `Vehicle photo ${currentIndex + 1}`}
                className="w-full h-full object-contain"
                loading="eager"
                fetchpriority="high"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                onError={(e) => {
                  e.target.src = '/assets/assets/images/business-shop-sign.webp';
                }}
              />
            )}
          </AnimatePresence>
          
          {/* Navigation Arrows */}
          <motion.button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaChevronLeft className="text-xl" />
          </motion.button>
          
          <motion.button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaChevronRight className="text-xl" />
          </motion.button>
          
          {/* Fullscreen Button (only for images) */}
          {galleryMedia[currentIndex]?.type === 'image' && (
            <motion.button
              onClick={() => setFullscreenImage(galleryMedia[currentIndex])}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaExpand className="text-xl" />
            </motion.button>
          )}
          
          {/* Media Counter */}
          <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {galleryMedia.length}
          </div>
        </motion.div>
        
        {/* Thumbnail Strip — limited to first few on initial render for prerender performance */}
        {galleryMedia.length > 1 && (
          <div className="mt-4 overflow-x-auto">
            <div className="flex gap-2 xl:gap-3 2xl:gap-4">
              {(showAllThumbnails ? galleryMedia : galleryMedia.slice(0, INITIAL_THUMB_COUNT)).map((media, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`relative flex-shrink-0 w-24 h-20 xl:w-32 xl:h-24 2xl:w-40 2xl:h-32 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                    currentIndex === index 
                      ? 'border-primary-500 shadow-lg' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {media.type === 'video' ? (
                    <>
                      <video
                        src={media.url}
                        className="w-full h-full object-contain"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <FaPlayCircle className="text-white text-2xl" />
                      </div>
                    </>
                  ) : (
                    <img
                      src={media.url}
                      alt={vehicleName ? `${vehicleName} - thumbnail ${index + 1}` : `Thumbnail ${index + 1}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/assets/assets/images/business-shop-sign.webp';
                      }}
                    />
                  )}
                  {currentIndex === index && (
                    <motion.div 
                      className="absolute inset-0 bg-primary-500/20"
                      layoutId="thumbnail-indicator"
                    />
                  )}
                </motion.button>
              ))}
              {!showAllThumbnails && galleryMedia.length > INITIAL_THUMB_COUNT && (
                <button
                  onClick={() => setShowAllThumbnails(true)}
                  className="flex-shrink-0 w-24 h-20 xl:w-32 xl:h-24 2xl:w-40 2xl:h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary-500 bg-gray-50 hover:bg-primary-50 flex items-center justify-center transition-all duration-300"
                >
                  <span className="text-sm font-semibold text-gray-600 hover:text-primary-600">+{galleryMedia.length - INITIAL_THUMB_COUNT} more</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Fullscreen Modal (only for images) */}
      <AnimatePresence>
        {fullscreenImage && fullscreenImage.type === 'image' && (
          <motion.div
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFullscreenImage(null)}
          >
            <motion.button
              className="absolute top-4 right-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
              onClick={() => setFullscreenImage(null)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaTimes className="text-2xl" />
            </motion.button>
            
            <motion.img
              src={fullscreenImage.url}
              alt={vehicleName ? `${vehicleName} - fullscreen view` : 'Fullscreen vehicle photo'}
              className="max-w-full max-h-full object-contain"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Navigation in Fullscreen */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                let prevIndex = currentIndex === 0 ? galleryMedia.length - 1 : currentIndex - 1;
                // Find previous image (skip videos)
                while (galleryMedia[prevIndex]?.type === 'video' && prevIndex !== currentIndex) {
                  prevIndex = prevIndex === 0 ? galleryMedia.length - 1 : prevIndex - 1;
                }
                if (galleryMedia[prevIndex]?.type === 'image') {
                  setCurrentIndex(prevIndex);
                  setFullscreenImage(galleryMedia[prevIndex]);
                }
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-4 hover:bg-white/10 rounded-full transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaChevronLeft className="text-3xl" />
            </motion.button>
            
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                let nextIndex = currentIndex === galleryMedia.length - 1 ? 0 : currentIndex + 1;
                // Find next image (skip videos)
                while (galleryMedia[nextIndex]?.type === 'video' && nextIndex !== currentIndex) {
                  nextIndex = nextIndex === galleryMedia.length - 1 ? 0 : nextIndex + 1;
                }
                if (galleryMedia[nextIndex]?.type === 'image') {
                  setCurrentIndex(nextIndex);
                  setFullscreenImage(galleryMedia[nextIndex]);
                }
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-4 hover:bg-white/10 rounded-full transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaChevronRight className="text-3xl" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageGallery;
